/**
 * Utilities for building the write-back trail modal data.
 * Fetches auth-on-file data from Postgres and transforms it into tab/image
 * structures consumed by {@link ImageViewerWithBadgesModal}.
 * @module createDataForWriteBackTrail
 */

import { singleOrderDetailsGenericCallForPostgres } from "../../../../api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  AuthOnFile,
  WriteBackTrail,
} from "../../../../data-model/medicalPaOrdersModel";
import { SingleOrderDetailsTableName } from "../../../../enums/singleOrderDetailsTableName";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../utils/stringModifications";
import {
  ImageViewerWithBadgesModalProps,
  ImageViewerWithBadgesProps,
} from "../imageViewerWithBadgesModal";

/**
 * Fetches auth-on-file data for an order from Postgres and transforms it
 * into the tab structure expected by the write-back trail modal.
 *
 * @param orderId - Order ID to fetch trail data for
 * @param authStatuses - Available auth status options used for badge styling
 * @param signal - Optional AbortSignal to cancel the in-flight request
 * @returns Object with a `tabs` array for the ImageViewerWithBadgesModal
 */
export const createDataForWriteBackTrail = async (
  orderId: string,
  authStatuses: AuthStatusOptionModel[],
  signal?: AbortSignal,
): Promise<{
  tabs: ImageViewerWithBadgesModalProps["tabs"];
}> => {
  if (!orderId || !authStatuses || authStatuses.length === 0) {
    return {
      tabs: [],
    };
  }
  try {
    const response = await singleOrderDetailsGenericCallForPostgres(
      {
        order_id: orderId,
        org_id: getOrgIdForFetchExternalWorklist(),
        table_name: SingleOrderDetailsTableName.AUTH_ON_FILE,
      },
      signal,
    );
    const { currentWriteBackImagesData, previousWriteBackImagesData } =
      generateWriteBackTrail(
        response as { auth_on_file: AuthOnFile },
        authStatuses,
      );
    const tabsWithData = generateTabsWithDataForWriteBackTrail({
      currentWriteBackImagesData,
      previousWriteBackImagesData,
    });
    return {
      tabs: tabsWithData,
    };
  } catch (error) {
    console.error("Error creating data for write back trail:", error);
    return {
      tabs: [],
    };
  }
};

/**
 * Wraps current and previous write-back image arrays into the tab structure
 * expected by the modal's `NewTabElements` component.
 *
 * @param data - Object containing current and previous write-back image arrays
 * @returns Array of tab objects with id, label, and images
 */
export const generateTabsWithDataForWriteBackTrail = (data: {
  currentWriteBackImagesData: ImageViewerWithBadgesProps[];
  previousWriteBackImagesData: ImageViewerWithBadgesProps[];
}): ImageViewerWithBadgesModalProps["tabs"] => {
  const tabsWithData: ImageViewerWithBadgesModalProps["tabs"] = [];
  tabsWithData.push({
    id: "current",
    label: "Current Comments",
    imagesWithBadges: data.currentWriteBackImagesData,
  });
  tabsWithData.push({
    id: "previous",
    label: "Previous Comments",
    imagesWithBadges: data.previousWriteBackImagesData,
  });
  return tabsWithData;
};

/**
 * Splits the write-back trail screenshots into "current" and "previous" buckets
 * based on the number of business-office-notes reports.
 *
 * @param data - Auth-on-file record containing reports and screenshots
 * @param authStatuses - Auth status options for mapping status IDs to display text/colors
 * @returns Object with `currentWriteBackImagesData` and `previousWriteBackImagesData` arrays
 */
export const generateWriteBackTrail = (
  data: { auth_on_file: AuthOnFile },
  authStatuses: AuthStatusOptionModel[],
) => {
  const currentWriteBackImagesData: ImageViewerWithBadgesProps[] = [];
  const previousWriteBackImagesData: ImageViewerWithBadgesProps[] = [];
  const busniessOfficeNotesLength = data?.auth_on_file?.reports?.length ?? 0;
  const writeBackTrail =
    data?.auth_on_file?.screenshots?.write_note_trails ?? [];
  const authStatusMap = buildAuthStatusMap(authStatuses);
  console.log("busniessOfficeNotesLength", busniessOfficeNotesLength);
  console.log("writeBackTrail", writeBackTrail);
  console.log("authStatusMap", authStatusMap);

  if (busniessOfficeNotesLength > 0 && writeBackTrail.length > 0) {
    const currentWriteBackTrails = writeBackTrail.slice(
      -busniessOfficeNotesLength,
    );
    console.log("currentWriteBackTrails", currentWriteBackTrails);

    const previousWriteBackTrails = writeBackTrail.slice(
      0,
      writeBackTrail.length - busniessOfficeNotesLength,
    );
    console.log("previousWriteBackTrails", previousWriteBackTrails);

    currentWriteBackTrails.forEach((trail) => {
      currentWriteBackImagesData.push(
        generateWriteBackTrailDataForComponent(trail, authStatusMap),
      );
    });
    previousWriteBackTrails.forEach((trail) => {
      previousWriteBackImagesData.push(
        generateWriteBackTrailDataForComponent(trail, authStatusMap),
      );
    });
  }

  return {
    currentWriteBackImagesData,
    previousWriteBackImagesData,
  };
};

const getAuthStatusText = (
  authStatus: string,
  authStatusMap: Map<string, AuthStatusOptionModel>,
) => {
  if (authStatus === "new") return "New";
  return (
    authStatusMap.get(authStatus)?.text ??
    capitalizeWordsSeperatedByUnderScore(authStatus)
  );
};

const getAuthStatusBgColor = (
  authStatus: string,
  authStatusMap: Map<string, AuthStatusOptionModel>,
) => {
  if (authStatus === "new") return "#EAF2FF";
  return authStatusMap.get(authStatus)?.bgColor ?? "#F5F5F5";
};

const getAuthStatusTextColor = (
  authStatus: string,
  authStatusMap: Map<string, AuthStatusOptionModel>,
) => {
  if (authStatus === "new") return "#0056D6";
  return authStatusMap.get(authStatus)?.textColor ?? "#0F0F0F";
};

const generateWriteBackTrailDataForComponent = (
  trail: WriteBackTrail,
  authStatusMap: Map<string, AuthStatusOptionModel>,
) => {
  return {
    imagePath: trail?.screenshot_path ?? "",
    fromBatchDetails: {
      batchText: getAuthStatusText(
        trail?.previous_auth_status ?? "",
        authStatusMap,
      ),
      batchBgColor: getAuthStatusBgColor(
        trail?.previous_auth_status ?? "",
        authStatusMap,
      ),
      batchTextColor: getAuthStatusTextColor(
        trail?.previous_auth_status ?? "",
        authStatusMap,
      ),
      batchId: trail?.report_identifier ?? "",
    },
    currentBatchDetails: {
      batchText: getAuthStatusText(trail?.new_auth_status ?? "", authStatusMap),
      batchBgColor: getAuthStatusBgColor(
        trail?.new_auth_status ?? "",
        authStatusMap,
      ),
      batchTextColor: getAuthStatusTextColor(
        trail?.new_auth_status ?? "",
        authStatusMap,
      ),
      batchId: trail?.report_identifier ?? "",
    },
    status_updated_at: trail?.status_updated_at ?? "",
  };
};

const buildAuthStatusMap = (authStatuses: AuthStatusOptionModel[]) => {
  const map = new Map<string, AuthStatusOptionModel>();
  for (const status of authStatuses) {
    map.set(status.id, status);
  }
  return map;
};
